import { createSelector } from '@reduxjs/toolkit';
import { getHistoryPrices, getPrices } from 'src/store/utils/balances';
import dayjs from '@skynet/formatting-date';
import { isComparisonDateNow } from 'src/store/shared/balance-history/helpers';
import type { NaiveLocalDateTime } from 'src/store/shared/balance-history/helpers';
import type {
  BalanceHistoryMap,
  BalanceHistoryState,
  InitialBalanceItem,
  InitialValuesMap,
  JsonBalanceHistoryItem,
} from './types';
import { selectBalancesSelection } from 'src/store/shared/balances/selectors';

import { flatMap, isEmpty, reduce, values, map } from 'es-toolkit/compat';

export interface Store {
  balanceHistory: BalanceHistoryState;
}

const defaultBalanceHistoryMap: BalanceHistoryMap = {};

const mergeBalanceHistory = (
  regular: BalanceHistoryMap = {},
  overrides: BalanceHistoryMap = {},
): BalanceHistoryMap => {
  if (isEmpty(regular)) {
    return defaultBalanceHistoryMap;
  }

  if (isEmpty(overrides)) {
    return regular;
  }

  return reduce<BalanceHistoryMap, BalanceHistoryMap>(
    regular,
    (acc, items, accountId) => {
      acc[accountId] = items;

      if (overrides[accountId]) {
        const overrideItems = overrides[accountId] ?? {};
        if (!isEmpty(overrideItems)) {
          acc[accountId] = {
            ...items,
            ...overrideItems,
          };
        }
      }

      return acc;
    },
    {} as BalanceHistoryMap,
  );
};

const selectRegular = (store: Store) => store.balanceHistory.regular;
const selectOverrides = (store: Store) => store.balanceHistory.overrides;
export const selectInitialValues = (store: Store) => store.balanceHistory.initialBalances;

export const selectInitialBalances = createSelector(selectInitialValues, (initialBalances) => {
  return reduce(
    initialBalances,
    (acc, balances, accountId) => {
      const balancesValues = values(balances).flat();
      const aggregatedBalances = reduce(
        balancesValues,
        (tokenAcc, balance) => {
          const token = balance.token;
          if (!tokenAcc[token]) {
            tokenAcc[token] = { ...balance, value: 0 };
          }
          tokenAcc[token].value += balance.value;
          return tokenAcc;
        },
        {} as Record<string, InitialBalanceItem>,
      );

      return {
        ...acc,
        [accountId]: aggregatedBalances,
      };
    },
    {} as InitialValuesMap,
  );
});

export const selectBalanceHistory = createSelector(
  selectRegular,
  selectOverrides,
  mergeBalanceHistory,
);

export const selectBalancePriceHistory = (store: Store) => store.balanceHistory.priceHistory;

const selectBalancePrices = (store: Store) => store.balanceHistory.prices;

export const selectIsBalanceHistoryLoading = (store: Store) =>
  store.balanceHistory.requestStatus === 'pending';

export const selectComparisonStartDate = (store: Store) => store.balanceHistory.startDate;
export const selectComparisonEndDate = (store: Store) => store.balanceHistory.endDate;

export const selectComparisonStartTime = (store: Store) => store.balanceHistory.startTime;
export const selectComparisonEndTime = (store: Store) => store.balanceHistory.endTime;

export const selectIsInitialBalanceModalOpen = (store: Store) =>
  store.balanceHistory.isInitialBalanceModalOpen;

export const selsectSetInitialBalanceInitialValues = (store: Store) =>
  store.balanceHistory.formSetInitialBalance;

export const selectIsInitialBalanceSubmitting = (store: Store) =>
  store.balanceHistory.resSetInitialBalance === 'pending';

export const selectIsInitialBalanceSubmitted = (store: Store) =>
  store.balanceHistory.resSetInitialBalance === 'success';

const selectBalanceHistoryList = createSelector(selectBalanceHistory, (history) => {
  return flatMap(history, (dateRecords, accountID) => {
    return flatMap(dateRecords, (items, date) => {
      return map(items, (item) => ({
        ...item,
        accountID,
        date,
      }));
    });
  });
});

export const selectPrices = createSelector(selectBalancePrices, getPrices);

const selectComparisonTimes = (
  comparisonDate: NaiveLocalDateTime | undefined,
  history: JsonBalanceHistoryItem[],
  { isEnd }: { isEnd: boolean },
) => {
  if (!comparisonDate || !history) {
    return [];
  }

  // Local, not dayjs.utc(): the picked value is later applied as local
  // wall-clock hours/minutes onto comparisonEndDate (useComparisonDates.ts
  // handleCompare*TimeChange), so the option list must be built in the same
  // local mode or a picked snapshot resolves to the wrong instant, and can
  // miss a real snapshot whose UTC calendar day differs from the viewer's
  // local one (SKY-4575).
  const historyValues = history.map((item) => dayjs(item.date));
  const isTodayCompare = comparisonDate && isComparisonDateNow(comparisonDate);

  const result = historyValues
    .sort((a, z) => (a.isBefore(z) ? -1 : 1))
    .reduce<{ label: string; value: string }[]>((acc, item) => {
      if (!item.isSame(dayjs(comparisonDate), 'date')) {
        return acc;
      }

      const time = item.format('HH:mm');

      if (acc.some((o) => o.value === time)) return acc;

      acc.push({
        label: time === '00:00' ? 'Midnight' : time,
        value: time,
      });

      return acc;
    }, []);

  // "Current" is an end time only: a start time is applied as hours/minutes
  // (useComparisonDates handleCompareStartTimeChange), so picking it there
  // requested an "Invalid Date" range.
  if (isEnd && isTodayCompare) {
    const current = { label: 'Current', value: 'current' };

    // The live balance always exists for today. It stands in for today's latest
    // snapshot, so it takes that slot — or is the only option when no snapshot
    // falls on this date yet: snapshots are keyed by UTC day, and the local date
    // can already be ahead of it (SKY-4575).
    if (result.length) {
      result[result.length - 1] = current;
    } else {
      result.push(current);
    }
  }

  return result;
};

export const selectBalanceHistoryEndSnapshotTimes = createSelector(
  selectComparisonEndDate,
  selectBalanceHistoryList,
  (comparisonDate, history) => selectComparisonTimes(comparisonDate, history, { isEnd: true }),
);

export const selectBalanceHistoryStartSnapshotTimes = createSelector(
  selectComparisonStartDate,
  selectBalanceHistoryList,
  (comparisonDate, history) => selectComparisonTimes(comparisonDate, history, { isEnd: false }),
);

/**
 * Creates the selector for the compare-view price series (see `getHistoryPrices`).
 *
 * @param selectTokenMap - Domain selector for the applied token map, so series are also keyed by
 * the labels the compare columns use.
 */
export const createSelectPriceHistory = <S>(selectTokenMap: (state: S) => Record<string, string>) =>
  createSelector(
    selectBalanceHistoryList,
    selectComparisonEndDate,
    selectBalancesSelection,
    selectBalancePriceHistory,
    selectComparisonEndTime,
    // The shared normalised live-quote map, so `getPrices` is not run a second time on the same
    // `balanceHistory.prices` input that `selectPrices` already memoises for the other views.
    selectPrices,
    selectTokenMap,
    getHistoryPrices,
  );
